import { useQuery } from '@tanstack/react-query'
import { LoadFailure, Skeleton } from '../../core/index.js'
import { ENGINE_FILTER, type FacilityActivityEntry } from '../types.js'
import type { FacilityActivityApi } from './facilityActivityApi.js'
import { describeEntry, formatWhen } from './activityText.js'

/** How often the log is asked again while on screen: the engine acts on every census, answer and hourly tick. */
const ENGINE_LOG_REFRESH_MS = 60_000
const NO_BEHAVIORS = new Map()

/** An entry's line with the worker's name in front where it is about one worker. */
function lineOf(entry: FacilityActivityEntry, names: ReadonlyMap<string, string>, units: ReadonlyMap<string, string> | undefined): string {
  const { text } = describeEntry(entry, NO_BEHAVIORS, units)
  const name = entry.workerId ? names.get(entry.workerId) : undefined
  return name ? `${name} ${text}` : text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * What the engine did (facility operations dashboard plan §3, D7): the facility's activity feed through its `engine`
 * filter - the census changes applied, releases, the open board's moves and the Bridge callouts - newest first, with
 * the day's count. The whole feed is on the Activity tab.
 */
export function EngineLog({ api, names, units, onOpenActivity }: { api: FacilityActivityApi; names: ReadonlyMap<string, string>; /** The facility's units by id, so a line names a unit rather than its id. */ units?: ReadonlyMap<string, string>; onOpenActivity?: () => void }) {
  const query = useQuery({
    queryKey: [...api.queryKey, 'engine-log'],
    queryFn: () => api.load({ since: null, filter: ENGINE_FILTER, cursor: null }),
    refetchInterval: ENGINE_LOG_REFRESH_MS,
  })
  if (query.isError) {
    return <LoadFailure size="xs" message="Could not load what the engine did." onRetry={() => void query.refetch()} retrying={query.isFetching} />
  }
  const entries = query.data?.activity.entries ?? []
  if (!query.data) {
    return <Skeleton />
  }
  if (entries.length === 0) {
    return <p className="text-xs text-ink-mid">Nothing yet in this window. Census changes applied, releases, the open board’s offers and fills, and Bridge callouts appear here as they happen.</p>
  }
  return (
    <div className="flex flex-col">
      <ul className="flex flex-col divide-y divide-marine/10">
        {entries.map((entry) => (
          <li key={entry.entryId} className="grid grid-cols-[5.5rem_1fr] gap-3 py-2 text-xs">
            <span className="font-bold text-ink-mid">{formatWhen(entry.at)}</span>
            <span className="text-ink">{lineOf(entry, names, units)}</span>
          </li>
        ))}
      </ul>
      {onOpenActivity && (
        <button type="button" className="mt-2 self-start text-xs font-bold text-marine hover:text-marine-dk" onClick={onOpenActivity}>
          {query.data.activity.engineCount !== undefined ? `All ${query.data.activity.engineCount} on the Activity tab` : 'All activity'}
        </button>
      )}
    </div>
  )
}
